import api from '@/lib/api';
import type {
  AccessReviewCampaign,
  ApiResponse,
  AuthAssignment,
  AuthPermission,
  AuthRoleDetail,
  AuthRoleSummary,
  CreateAssignmentRequest,
  CreateRbacRoleRequest,
  RbacApprovalRequest,
  RbacPermissionListQuery,
  RbacRoleListQuery,
  SetUserCustomRolesRequest,
  SimulateResult,
  UpdateRbacRoleRequest,
  UserCustomRole,
} from '@amuster/types';

export const rbacService = {
  listPermissions(query?: RbacPermissionListQuery) {
    return api.get<ApiResponse<AuthPermission[]>>('/rbac/permissions', { params: query });
  },

  listRoles(query?: RbacRoleListQuery) {
    return api.get<ApiResponse<AuthRoleSummary[]>>('/rbac/roles', { params: query });
  },

  getRoleById(id: string) {
    return api.get<ApiResponse<AuthRoleDetail>>(`/rbac/roles/${id}`);
  },

  createRole(data: CreateRbacRoleRequest) {
    return api.post<ApiResponse<AuthRoleDetail>>('/rbac/roles', data);
  },

  updateRole(id: string, data: UpdateRbacRoleRequest) {
    return api.put<ApiResponse<AuthRoleDetail>>(`/rbac/roles/${id}`, data);
  },

  deleteRole(id: string, options?: { force?: boolean }) {
    return api.delete<ApiResponse<{ message: string; affected_users_count: number }>>(
      `/rbac/roles/${id}`,
      { params: options?.force ? { force: 'true' } : undefined }
    );
  },

  listUserCustomRoles(userId: string) {
    return api.get<ApiResponse<UserCustomRole[]>>(`/rbac/users/${userId}/custom-roles`);
  },

  setUserCustomRoles(userId: string, data: SetUserCustomRolesRequest) {
    return api.put<ApiResponse<UserCustomRole[]>>(`/rbac/users/${userId}/custom-roles`, data);
  },

  listUserAssignments(userId: string) {
    return api.get<ApiResponse<AuthAssignment[]>>(`/rbac/users/${userId}/assignments`);
  },

  createAssignment(userId: string, data: CreateAssignmentRequest) {
    return api.post<ApiResponse<AuthAssignment>>(`/rbac/users/${userId}/assignments`, data);
  },

  revokeAssignment(userId: string, assignmentId: string) {
    return api.delete<ApiResponse<{ message: string }>>(`/rbac/users/${userId}/assignments/${assignmentId}`);
  },

  // ── Effective Access & History ─────────────────────────────────────────────

  getEffectiveAccess(userId: string) {
    return api.get<ApiResponse<unknown[]>>(`/rbac/users/${userId}/effective-access`);
  },

  getAccessHistory(userId: string) {
    return api.get<ApiResponse<unknown[]>>(`/rbac/users/${userId}/access-history`);
  },

  // ── Simulator ───────────────────────────────────────────────────────────

  simulate(data: { user_id: string; permission_key: string; scope_type?: string; scope_id?: string }) {
    return api.post<ApiResponse<SimulateResult>>('/rbac/simulate', data);
  },

  // ── Approval Queue ──────────────────────────────────────────────────────

  listApprovalRequests(status?: string) {
    return api.get<ApiResponse<RbacApprovalRequest[]>>('/rbac/approval-requests', {
      params: status ? { status } : undefined,
    });
  },

  decideApproval(id: string, data: { decision: 'approved' | 'rejected'; reason?: string }) {
    return api.post<ApiResponse<RbacApprovalRequest>>(`/rbac/approval-requests/${id}/decide`, data);
  },

  // ── Access Reviews ──────────────────────────────────────────────────────

  listAccessReviewCampaigns() {
    return api.get<ApiResponse<AccessReviewCampaign[]>>('/rbac/access-reviews');
  },

  createAccessReview(data: { name: string; description?: string; starts_at: string; ends_at: string }) {
    return api.post<ApiResponse<AccessReviewCampaign>>('/rbac/access-reviews', data);
  },

  listCampaignAssignments(campaignId: string) {
    return api.get<ApiResponse<unknown[]>>(`/rbac/access-reviews/${campaignId}/assignments`);
  },

  submitAttestation(campaignId: string, data: { assignment_id: string; decision: 'confirm' | 'revoke' }) {
    return api.post<ApiResponse<{ message: string }>>(`/rbac/access-reviews/${campaignId}/attestations`, data);
  },
};

